// Fase 17.3, Plano 06 — ficha da peça (D-14, D-15, D-19..D-22): dados técnicos, roteiro e histórico de versões numa tela só.
// Quem tem roteiros:gerenciar monta o roteiro (modelo, cópia ou etapa por etapa) e salva uma nova versão com motivo;
// os demais perfis só leem (D-16). "Gerar OP" dos lotes que aguardavam roteiro exige pv:ajustar (RT-05).
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CodigoLegadoDto, FichaPecaRoteiroDto, LoteAguardandoRoteiroDto, RoteiroVersaoResumoDto, ServicoComCelulasDto, TintaRendimentoDto, TratamentoPecaDto } from '@pieltec/contracts';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { CampoSelect } from '../../../components/ui/campo-select';
import { Card, CardContent } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { Skeleton } from '../../../components/ui/skeleton';
import { Textarea } from '../../../components/ui/textarea';
import { Input } from '../../../components/ui/input';
import { etapasDaVersao, etapasParaSalvar, novaEtapa, validarEtapas, type EtapaEdicao } from '../../../lib/roteiros/etapas';
import { podeVoltarAoModelo } from '../../../lib/roteiros/modelos';
import { voltarAoModelo } from '../../../lib/roteiros/modelos-api';
import { ErroHttpApi } from '../../../lib/csrf-fetch';
import { alterarAtivacaoTratamento, buscarFicha, ErroRoteiroApi, gerarOpDoLote, salvarRoteiro, salvarTratamento, servicosComCelulas } from '../../../lib/roteiros/roteiros-api';
import { obterSessaoAtual } from '../../../lib/session';
import { camposPendentesConsumo } from '../../../lib/roteiros/dados-tecnicos';
import { DadosTecnicosForm } from './dados-tecnicos-form';
import { EditorEtapas } from './editor-etapas';
import { IniciarRoteiroDialog, type InicioDeRoteiro } from './iniciar-roteiro-dialog';
import { TintaDaPecaBloco } from './tinta-da-peca';
import { listarTintas } from '../../../lib/roteiros/tintas-api';

const MOTIVO_MINIMO = 5;
export const ROTULO_VOLTAR_AO_MODELO = 'Voltar a seguir o modelo';
export const NOTA_VERSAO_OPS = 'OPs já liberadas mantêm a versão delas. OPs ainda em rascunho vão usar a versão vigente no momento da liberação.';

const ROTULO_SITUACAO_VERSAO: Readonly<Record<RoteiroVersaoResumoDto['situacao'], string>> = {
  DEFINITIVO: 'Definitiva',
  PROVISORIO: 'Provisória',
};

export function formatarDataHora(texto: string): string {
  const data = new Date(texto.includes('T') ? texto : texto.replace(' ', 'T'));
  return Number.isNaN(data.getTime()) ? texto : data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Id da peça no fim da URL; `''` quando não há ou quando o `%` é malformado (a tela mostra "Peça não encontrada" em vez de quebrar). */
export function idDaPeca(pathname: string): string {
  const ultimo = pathname.split('/').filter(Boolean).at(-1) ?? '';
  try {
    return decodeURIComponent(ultimo);
  } catch {
    return '';
  }
}

export const MENSAGEM_PECA_NAO_ENCONTRADA = 'Peça não encontrada.';
export const MENSAGEM_SEM_PERMISSAO_FICHA = 'Você não tem permissão para ver esta peça.';
export const MENSAGEM_FALHA_AO_CARREGAR_FICHA = 'Não foi possível carregar a peça. Verifique a conexão e tente novamente.';

/** Decide pelo status HTTP, não pelo texto: 404 e 400 (id que não é UUID) = não existe; 403 = sem permissão; o resto é falha real. */
export function mensagemDeFalhaAoCarregarFicha(erro: unknown): string {
  if (erro instanceof ErroHttpApi) {
    if (erro.status === 404 || erro.status === 400) return MENSAGEM_PECA_NAO_ENCONTRADA;
    if (erro.status === 403) return MENSAGEM_SEM_PERMISSAO_FICHA;
  }
  return MENSAGEM_FALHA_AO_CARREGAR_FICHA;
}

function HistoricoVersoes({ versoes }: { readonly versoes: readonly RoteiroVersaoResumoDto[] }) {
  if (versoes.length === 0) return <p className="text-sm text-muted-foreground">Nenhuma versão salva ainda.</p>;
  return (
    <ul className="flex flex-col gap-2" aria-label="Histórico de versões">
      {versoes.map((versao) => (
        <li key={versao.id} className="flex flex-col gap-1 rounded-md border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">Versão {versao.versao}</span>
            <Badge variant="outline">{ROTULO_SITUACAO_VERSAO[versao.situacao]}</Badge>
            <span className="text-sm text-muted-foreground">{versao.responsavelNome ?? 'Responsável não identificado'} · {formatarDataHora(versao.createdAt)}</span>
          </div>
          <p className="text-sm">{versao.motivo}</p>
        </li>
      ))}
    </ul>
  );
}

/** Selos do cabeçalho: origem do roteiro, dados incompletos e vínculo com o modelo (D-02, D-17, D-19). */
export function SelosDaFicha({ ficha }: { readonly ficha: Pick<FichaPecaRoteiroDto, 'roteiro' | 'dadosTecnicos' | 'consumoTintaEstimado'> }) {
  const roteiro = ficha.roteiro;
  const pendentes = camposPendentesConsumo(ficha.dadosTecnicos, ficha.consumoTintaEstimado?.origemTinta === 'PECA');
  return (
    <div className="flex flex-wrap items-center gap-2">
      {roteiro?.origem === 'cargamaquina' ? <Badge variant="outline">Importado do CargaMáquina</Badge> : null}
      {pendentes.length > 0 ? <span className="text-sm font-semibold">Nos dados salvos, falta informar: {pendentes.map((campo) => campo.rotulo).join(' e ')}.</span> : null}
      {roteiro?.modelo ? <span className="text-sm text-muted-foreground">Modelo: {roteiro.modelo.nome}</span> : null}
      {roteiro?.modelo && roteiro.difereDoModelo ? <Badge variant="outline">Difere do modelo</Badge> : null}
    </div>
  );
}

/** Códigos antigos do CargaMáquina, com a etapa e o serviço de onde vieram (D-07). */
export function CodigosLegados({ codigos }: { readonly codigos: readonly CodigoLegadoDto[] }) {
  if (codigos.length === 0) return <p className="text-sm text-muted-foreground">Nenhum código legado vinculado.</p>;
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Códigos legados">
      {codigos.map((codigo) => (
        <li key={codigo.codigo}>
          <Badge variant="outline">
            {codigo.codigo}{codigo.etapaOrdem !== null ? ` · etapa ${codigo.etapaOrdem}` : ''}{codigo.servicoCodigo ? ` · ${codigo.servicoCodigo}` : ''}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

function TratamentosDaPeca({ tratamentos, podeGerenciar, onRevisar, onAlternar }: { readonly tratamentos: readonly TratamentoPecaDto[]; readonly podeGerenciar: boolean; readonly onRevisar: (tratamento: TratamentoPecaDto) => void; readonly onAlternar: (tratamento: TratamentoPecaDto) => void }) {
  if (tratamentos.length === 0) return <p className="text-sm text-muted-foreground">Nenhum tratamento específico por cliente foi cadastrado.</p>;
  return <ul className="flex flex-col gap-2" aria-label="Tratamentos por cliente">{tratamentos.map((tratamento) => {
    const atual = tratamento.vigente;
    return <li key={tratamento.id} className="rounded-md border p-4 text-sm"><div className="flex flex-wrap items-center justify-between gap-2"><p className="font-semibold">{tratamento.clienteNome} · {tratamento.codigo} — {tratamento.nome} {!tratamento.ativo && <Badge variant="outline">Inativo</Badge>}</p>{podeGerenciar ? <div className="flex gap-2"><Button type="button" size="sm" variant="outline" onClick={() => onRevisar(tratamento)}>Revisar</Button><Button type="button" size="sm" variant="outline" onClick={() => onAlternar(tratamento)}>{tratamento.ativo ? 'Desativar' : 'Ativar'}</Button></div> : null}</div>{atual ? <p className="text-muted-foreground">v{atual.versao} · roteiro {atual.roteiroId} · {atual.cor ?? 'cor não definida'}{atual.tipoTinta ? ` · ${atual.tipoTinta}` : ''}{atual.exigeIsolamento ? ' · exige isolamento' : ''}{atual.requisitos ? ` · ${atual.requisitos}` : ''}</p> : null}</li>;
  })}</ul>;
}

/** Lotes que esperam o roteiro; "Gerar OP" só com pv:ajustar e depois que a peça tem roteiro vigente (RT-05). */
export function LotesAguardando({ lotes, podeGerarOp, gerandoLoteId, onGerarOp }: {
  readonly lotes: readonly LoteAguardandoRoteiroDto[];
  readonly podeGerarOp: boolean;
  readonly gerandoLoteId?: string | null;
  readonly onGerarOp?: (loteId: string) => void;
}) {
  if (lotes.length === 0) return null;
  return (
    <div className="flex flex-col gap-4 rounded-md border p-4" role="region" aria-label="Lotes aguardando roteiro">
      <p className="text-sm font-semibold">{lotes.length === 1 ? '1 lote aguardando roteiro' : `${lotes.length} lotes aguardando roteiro`}</p>
      <ul className="flex flex-col gap-2">
        {lotes.map((lote) => (
          <li key={lote.pendenciaId} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm">{lote.loteCodigo ?? lote.loteId} · aberta em {formatarDataHora(lote.abertaEm)}</span>
            {podeGerarOp ? (
              <Button type="button" size="sm" disabled={gerandoLoteId === lote.loteId} onClick={() => onGerarOp?.(lote.loteId)}>
                {gerandoLoteId === lote.loteId ? 'Gerando...' : 'Gerar OP'}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PecaCadastroPage() { return <PecaRoteiroPage cadastro />; }

export function PecaRoteiroPage({ cadastro = false }: { readonly cadastro?: boolean }) {
  const listaHref = cadastro ? `/cadastros/pecas${window.location.search}` : '/pcp/roteiros';
  const materialId = useMemo(() => idDaPeca(window.location.pathname), []);
  const [ficha, setFicha] = useState<FichaPecaRoteiroDto | null>(null);
  const [servicos, setServicos] = useState<readonly ServicoComCelulasDto[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarregar, setErroCarregar] = useState<string | null>(null);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [edicao, setEdicao] = useState<EtapaEdicao[] | null>(null);
  const [mostrarErros, setMostrarErros] = useState(false);
  const [salvarAberto, setSalvarAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  // Modelo a gravar com a nova versão: undefined = manter o vínculo atual; null = sem modelo; id = seguir esse modelo.
  const [modeloAGravar, setModeloAGravar] = useState<string | null | undefined>(undefined);
  const [inicioAberto, setInicioAberto] = useState(false);
  const [gerandoLoteId, setGerandoLoteId] = useState<string | null>(null);
  const [erroOp, setErroOp] = useState<string | null>(null);
  const [voltarAberto, setVoltarAberto] = useState(false);
  const [motivoVoltar, setMotivoVoltar] = useState('');
  const [voltando, setVoltando] = useState(false);
  const [erroVoltar, setErroVoltar] = useState<string | null>(null);
  const [tratamentoAberto, setTratamentoAberto] = useState(false);
  const [tratamentoEmEdicao, setTratamentoEmEdicao] = useState<TratamentoPecaDto | null>(null);
  const [tratamentoClienteId, setTratamentoClienteId] = useState('');
  const [tratamentoCodigo, setTratamentoCodigo] = useState('');
  const [tratamentoNome, setTratamentoNome] = useState('');
  const [tratamentoCor, setTratamentoCor] = useState('');
  const [tratamentoTipoTinta, setTratamentoTipoTinta] = useState('');
  const [tratamentoTintaMaterialId, setTratamentoTintaMaterialId] = useState('');
  const [tintasElegiveis, setTintasElegiveis] = useState<readonly TintaRendimentoDto[]>([]);
  const [tratamentoRoteiroVersaoId, setTratamentoRoteiroVersaoId] = useState('');
  const [tratamentoIsolamento, setTratamentoIsolamento] = useState(false);
  const [tratamentoRequisitos, setTratamentoRequisitos] = useState('');
  const [tratamentoMotivo, setTratamentoMotivo] = useState('');
  const [erroTratamento, setErroTratamento] = useState<string | null>(null);
  const [salvandoTratamento, setSalvandoTratamento] = useState(false);

  // A autorização real é do servidor; aqui as ações de escrita só aparecem para quem pode (D-16).
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo && sessao.autenticado) setPermissoes(sessao.usuario.permissions);
    });
    return () => { vivo = false; };
  }, []);

  const carregar = useCallback(async (sinal?: { vivo: boolean }) => {
    if (materialId === '') {
      setErroCarregar(MENSAGEM_PECA_NAO_ENCONTRADA);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    setErroCarregar(null);
    try {
      const [fichaCarregada, servicosCarregados] = await Promise.all([buscarFicha(materialId), servicosComCelulas()]);
      if (sinal && !sinal.vivo) return;
      setFicha(fichaCarregada);
      setServicos(servicosCarregados);
    } catch (erro) {
      if (!sinal || sinal.vivo) setErroCarregar(mensagemDeFalhaAoCarregarFicha(erro));
    } finally {
      if (!sinal || sinal.vivo) setCarregando(false);
    }
  }, [materialId]);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => { sinal.vivo = false; };
  }, [carregar]);

  const podeGerenciar = permissoes.includes('roteiros:gerenciar');
  useEffect(() => {
    if (!tratamentoAberto) return;
    let vivo = true;
    void listarTintas().then((resultado) => { if (vivo) setTintasElegiveis(resultado.tintas); }).catch(() => { if (vivo) setTintasElegiveis([]); });
    return () => { vivo = false; };
  }, [tratamentoAberto]);
  useEffect(() => {
    if (!ficha || carregando) return;
    const ancora = window.location.hash.slice(1);
    if (ancora === 'dados-da-peca' || ancora === 'roteiro') document.getElementById(ancora)?.scrollIntoView();
  }, [ficha, carregando]);
  const podeGerarOp = permissoes.includes('pv:ajustar');
  const vigente = ficha?.roteiro?.vigente ?? null;
  const etapasVigentes = useMemo(() => (vigente ? etapasDaVersao(vigente.etapas, servicos) : []), [vigente, servicos]);

  const iniciarEdicao = (): void => {
    setSucesso(null);
    setMostrarErros(false);
    setModeloAGravar(undefined);
    setEdicao(vigente ? etapasDaVersao(vigente.etapas, servicos) : servicos[0] ? [novaEtapa(servicos[0])] : []);
  };
  const cancelarEdicao = (): void => { setEdicao(null); setMostrarErros(false); setModeloAGravar(undefined); };
  const comecarPor = (inicio: InicioDeRoteiro): void => {
    setSucesso(null);
    setMostrarErros(false);
    setModeloAGravar(inicio.modeloId);
    setEdicao(inicio.etapas);
  };

  const gerarOp = async (loteId: string): Promise<void> => {
    setGerandoLoteId(loteId);
    setErroOp(null);
    setSucesso(null);
    try {
      const resultado = await gerarOpDoLote(loteId);
      await carregar();
      setSucesso(`OP ${resultado.opNumero} gerada.`);
    } catch (erro) {
      setErroOp(erro instanceof Error ? erro.message : 'Não foi possível gerar a OP. Tente novamente.');
    } finally {
      setGerandoLoteId(null);
    }
  };

  const confirmarVoltar = async (): Promise<void> => {
    if (!ficha) return;
    setVoltando(true);
    setErroVoltar(null);
    try {
      const resultado = await voltarAoModelo(ficha.materialId, { motivo: motivoVoltar.trim() });
      setVoltarAberto(false);
      setMotivoVoltar('');
      await carregar();
      setSucesso(`Versão ${resultado.versao} salva. A peça voltou a seguir o modelo.`);
    } catch (erro) {
      setErroVoltar(erro instanceof Error ? erro.message : 'Não foi possível voltar a seguir o modelo. Tente novamente.');
    } finally {
      setVoltando(false);
    }
  };

  const pedirSalvar = (): void => {
    if (!edicao) return;
    if (validarEtapas(edicao).length > 0) { setMostrarErros(true); return; }
    setErroSalvar(null);
    setSalvarAberto(true);
  };

  const confirmarSalvar = async (): Promise<void> => {
    if (!edicao || !ficha) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const resultado = await salvarRoteiro(ficha.materialId, {
        etapas: etapasParaSalvar(edicao),
        motivo: motivo.trim(),
        versaoEsperada: vigente?.versao ?? 0,
        ...(modeloAGravar !== undefined ? { modeloId: modeloAGravar } : {}),
      });
      setSalvarAberto(false);
      setMotivo('');
      setEdicao(null);
      setMostrarErros(false);
      setModeloAGravar(undefined);
      await carregar();
      setSucesso(`Versão ${resultado.versao} salva.`);
    } catch (erro) {
      setErroSalvar(erro instanceof ErroRoteiroApi || erro instanceof Error ? erro.message : 'Não foi possível salvar o roteiro. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const abrirTratamento = (tratamento?: TratamentoPecaDto): void => {
    const atual = tratamento?.vigente;
    setTratamentoEmEdicao(tratamento ?? null); setTratamentoClienteId(tratamento?.clienteId ?? ficha?.clientes?.[0]?.clienteId ?? '');
    setTratamentoCodigo(tratamento?.codigo ?? ''); setTratamentoNome(tratamento?.nome ?? ''); setTratamentoCor(atual?.cor ?? ''); setTratamentoTipoTinta(atual?.tipoTinta ?? ''); setTratamentoTintaMaterialId(atual?.tintaMaterialId ?? ''); setTratamentoRoteiroVersaoId(atual?.roteiroVersaoId ?? vigente?.id ?? '');
    setTratamentoIsolamento(atual?.exigeIsolamento ?? false); setTratamentoRequisitos(atual?.requisitos ?? ''); setTratamentoMotivo(''); setErroTratamento(null); setTratamentoAberto(true);
  };
  const salvarTratamentoDaFicha = async (): Promise<void> => {
    if (!ficha || !tratamentoRoteiroVersaoId || !tratamentoClienteId || !tratamentoCodigo.trim() || !tratamentoNome.trim() || tratamentoMotivo.trim().length < MOTIVO_MINIMO) { setErroTratamento('Cliente, código, nome, roteiro e motivo de ao menos 5 caracteres são obrigatórios.'); return; }
    setSalvandoTratamento(true); setErroTratamento(null);
    try {
      const resultado = await salvarTratamento(ficha.materialId, { clienteId: tratamentoClienteId, codigo: tratamentoCodigo.trim(), nome: tratamentoNome.trim(), roteiroVersaoId: tratamentoRoteiroVersaoId, cor: tratamentoCor.trim() || null, tipoTinta: tratamentoTipoTinta.trim() || null, tintaMaterialId: tratamentoTintaMaterialId || null, exigeIsolamento: tratamentoIsolamento, requisitos: tratamentoRequisitos.trim() || null, motivo: tratamentoMotivo.trim(), ...(tratamentoEmEdicao ? { versaoEsperada: tratamentoEmEdicao.versao } : {}) });
      setTratamentoAberto(false); await carregar(); setSucesso(`Tratamento ${resultado.codigo} salvo na versão ${resultado.vigente?.versao ?? 1}.`);
    } catch (erro) { setErroTratamento(erro instanceof Error ? erro.message : 'Não foi possível salvar o tratamento.'); } finally { setSalvandoTratamento(false); }
  };
  const alternarTratamento = async (tratamento: TratamentoPecaDto): Promise<void> => {
    if (!ficha) return;
    try { await alterarAtivacaoTratamento(ficha.materialId, tratamento.id, { ativo: !tratamento.ativo, versaoEsperada: tratamento.versao }); await carregar(); setSucesso(`Tratamento ${tratamento.ativo ? 'desativado' : 'ativado'}.`); }
    catch (erro) { setErroTratamento(erro instanceof Error ? erro.message : 'Não foi possível alterar o tratamento.'); }
  };

  if (carregando && ficha === null) {
    return <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-6" aria-busy="true" aria-label="Carregando a peça">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>;
  }
  if (erroCarregar || !ficha) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-destructive">{erroCarregar ?? 'Peça não encontrada.'}</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => void carregar()}>Tentar novamente</Button>
              <Button asChild type="button" variant="outline"><a href={listaHref}>{cadastro ? 'Voltar às peças' : 'Voltar aos roteiros'}</a></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const emEdicao = edicao !== null;
  const motivoValido = motivo.trim().length >= MOTIVO_MINIMO;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <a className="text-sm text-muted-foreground underline-offset-4 hover:underline" href={listaHref}>{cadastro ? 'Peças / Produtos' : 'Roteiros das peças'}</a>
              <h1 className="text-[28px] font-semibold leading-tight">{ficha.codigo}</h1>
              <p className="text-sm text-muted-foreground">{ficha.nome}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={ficha.ativo ? 'secondary' : 'outline'}>{ficha.ativo ? 'Peça ativa' : 'Peça inativa'}</Badge>
              <Badge variant={vigente ? 'secondary' : 'destructive'}>{vigente ? `Roteiro v${vigente.versao}` : 'Sem roteiro'}</Badge>
            </div>
          </div>
          <SelosDaFicha ficha={ficha} />
          <div className="flex flex-wrap gap-2"><Button asChild variant="outline"><a href="#dados-da-peca">Dados da peça</a></Button><Button asChild variant="outline"><a href="#roteiro">Roteiro</a></Button></div>
          <div className="text-sm">{ficha.clientes?.length ? ficha.clientes.map((cliente) => <p key={`${cliente.clienteId}:${cliente.codigoItemCliente}`}>{cliente.clienteNome} · Código do cliente: {cliente.codigoItemCliente}</p>) : <p className="text-muted-foreground">Vínculo com cliente não confirmado.</p>}</div>
        </CardContent>
      </Card>

      {ficha.lotesAguardandoRoteiro.length > 0 ? (
        <div className="flex flex-col gap-2">
          <LotesAguardando lotes={ficha.lotesAguardandoRoteiro} podeGerarOp={podeGerarOp && vigente !== null} gerandoLoteId={gerandoLoteId} onGerarOp={(loteId) => void gerarOp(loteId)} />
          {erroOp ? <p role="alert" className="text-sm text-destructive">{erroOp}</p> : null}
        </div>
      ) : null}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h2 id="dados-da-peca" className="scroll-mt-24 text-sm font-semibold uppercase tracking-wider">Dados técnicos</h2>
          <DadosTecnicosForm materialId={ficha.materialId} dados={ficha.dadosTecnicos} somenteLeitura={!podeGerenciar} consumoEstimado={ficha.consumoTintaEstimado} corSugerida={ficha.corSugerida} onSalvo={() => void carregar()} />
           <TintaDaPecaBloco materialId={ficha.materialId} tintaDaPeca={ficha.tintaDaPeca} tipoDaPeca={ficha.dadosTecnicos.tipoTinta} podeGerenciar={podeGerenciar} onAlterada={() => void carregar()} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2"><h2 id="tratamentos" className="scroll-mt-24 text-sm font-semibold uppercase tracking-wider">Tratamentos por cliente</h2>{podeGerenciar ? <Button type="button" size="sm" onClick={() => abrirTratamento()}>Novo tratamento</Button> : null}</div>
          {erroTratamento ? <p role="alert" className="text-sm text-destructive">{erroTratamento}</p> : null}
          <TratamentosDaPeca tratamentos={ficha.tratamentos ?? []} podeGerenciar={podeGerenciar} onRevisar={abrirTratamento} onAlternar={(tratamento) => void alternarTratamento(tratamento)} />
        </CardContent>
      </Card>

      <FormDialog open={tratamentoAberto} onOpenChange={setTratamentoAberto}><FormDialogContent><FormDialogHeader><FormDialogTitle>{tratamentoEmEdicao ? 'Revisar tratamento' : 'Novo tratamento'}</FormDialogTitle><FormDialogDescription>Uma revisão fixa a versão escolhida do roteiro e não altera OPs já liberadas.</FormDialogDescription></FormDialogHeader><FormDialogBody className="grid gap-4"><label className="grid gap-1 text-sm">Cliente<CampoSelect value={tratamentoClienteId} disabled={tratamentoEmEdicao !== null} onValueChange={setTratamentoClienteId} options={(ficha.clientes ?? []).map((cliente) => ({ value: cliente.clienteId, label: cliente.clienteNome }))} /></label><label className="grid gap-1 text-sm">Roteiro da receita<CampoSelect value={tratamentoRoteiroVersaoId} onValueChange={setTratamentoRoteiroVersaoId} options={(ficha.roteiro?.versoes ?? []).map((versao) => ({ value: versao.id, label: `Versão ${versao.versao} · ${versao.motivo}` }))} placeholder="Selecione a versão" /></label><label className="grid gap-1 text-sm">Código<Input value={tratamentoCodigo} disabled={tratamentoEmEdicao !== null} onChange={(event) => setTratamentoCodigo(event.target.value)} /></label><label className="grid gap-1 text-sm">Nome<Input value={tratamentoNome} onChange={(event) => setTratamentoNome(event.target.value)} /></label><label className="grid gap-1 text-sm">Cor<Input value={tratamentoCor} onChange={(event) => setTratamentoCor(event.target.value)} /></label><label className="grid gap-1 text-sm">Tipo de tinta<Input value={tratamentoTipoTinta} onChange={(event) => setTratamentoTipoTinta(event.target.value)} /></label><label className="grid gap-1 text-sm">Tinta exata<CampoSelect value={tratamentoTintaMaterialId} onValueChange={setTratamentoTintaMaterialId} placeholder="Resolver pela cor e tipo" options={[{ value: '', label: 'Resolver pela cor e tipo' }, ...tintasElegiveis.map((tinta) => ({ value: tinta.materialId, label: `${tinta.codigo} · ${tinta.nome} · ${tinta.tipoTinta}${tinta.corCodigo ? ` · ${tinta.corCodigo}` : ''}` })), ...(tratamentoTintaMaterialId && !tintasElegiveis.some((tinta) => tinta.materialId === tratamentoTintaMaterialId) ? [{ value: tratamentoTintaMaterialId, label: 'Tinta vinculada (indisponível para nova seleção)' }] : [])]} /></label><label className="flex items-center gap-2 text-sm"><Checkbox checked={tratamentoIsolamento} onCheckedChange={(valor) => setTratamentoIsolamento(valor === true)} />Exige isolamento</label><label className="grid gap-1 text-sm">Requisitos<Textarea value={tratamentoRequisitos} onChange={(event) => setTratamentoRequisitos(event.target.value)} /></label><label className="grid gap-1 text-sm">Motivo da revisão<Textarea value={tratamentoMotivo} onChange={(event) => setTratamentoMotivo(event.target.value)} /></label></FormDialogBody><FormDialogFooter><Button type="button" variant="outline" onClick={() => setTratamentoAberto(false)}>Cancelar</Button><Button type="button" disabled={salvandoTratamento} onClick={() => void salvarTratamentoDaFicha()}>{salvandoTratamento ? 'Salvando...' : 'Salvar tratamento'}</Button></FormDialogFooter></FormDialogContent></FormDialog>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <h2 id="roteiro" className="scroll-mt-24 text-sm font-semibold uppercase tracking-wider">Roteiro</h2>
            <div className="flex flex-wrap gap-2">
              {podeGerenciar && !emEdicao ? (
                <Button type="button" onClick={vigente ? iniciarEdicao : () => setInicioAberto(true)}>{vigente ? 'Editar roteiro' : 'Definir roteiro'}</Button>
              ) : null}
              {podeGerenciar && emEdicao ? <Button type="button" variant="outline" onClick={() => setInicioAberto(true)}>Recomeçar a partir de…</Button> : null}
              {!emEdicao && podeVoltarAoModelo(ficha, permissoes) ? (
                <Button type="button" variant="outline" onClick={() => { setErroVoltar(null); setVoltarAberto(true); }}>{ROTULO_VOLTAR_AO_MODELO}</Button>
              ) : null}
            </div>
          </div>
          {sucesso ? <p role="status" className="text-sm font-semibold">{sucesso}</p> : null}
          {emEdicao ? (
            <>
              <EditorEtapas etapas={edicao} servicos={servicos} onChange={setEdicao} mostrarErros={mostrarErros} />
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={pedirSalvar}>Salvar nova versão</Button>
                  <Button type="button" variant="outline" onClick={cancelarEdicao}>Cancelar</Button>
                </div>
                <p className="text-xs text-muted-foreground">{NOTA_VERSAO_OPS}</p>
              </div>
            </>
          ) : vigente ? (
            <>
              <p className="text-sm text-muted-foreground">
                Versão {vigente.versao} · {ROTULO_SITUACAO_VERSAO[vigente.situacao]} · {vigente.motivo} · {formatarDataHora(vigente.createdAt)}
              </p>
              <EditorEtapas etapas={etapasVigentes} servicos={servicos} onChange={() => undefined} somenteLeitura />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Esta peça ainda não tem roteiro.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider">Códigos legados</h2>
          <CodigosLegados codigos={ficha.codigosLegados} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider">Histórico de versões</h2>
          <HistoricoVersoes versoes={ficha.roteiro?.versoes ?? []} />
        </CardContent>
      </Card>

      <IniciarRoteiroDialog aberto={inicioAberto} onAbertoChange={setInicioAberto} servicos={servicos} materialIdAtual={ficha.materialId} onEscolher={comecarPor} />

      <FormDialog open={voltarAberto} onOpenChange={(aberto) => { if (!voltando) setVoltarAberto(aberto); }}>
        <FormDialogContent>
          <FormDialogHeader>
            <FormDialogTitle>{ROTULO_VOLTAR_AO_MODELO}</FormDialogTitle>
            <FormDialogDescription>
              {`A peça ganha uma versão nova com as etapas do modelo${ficha.roteiro?.modelo ? ` ${ficha.roteiro.modelo.nome}` : ''}, e o ajuste próprio deixa de valer. As versões anteriores continuam no histórico.`}
            </FormDialogDescription>
          </FormDialogHeader>
          <FormDialogBody>
            <div className="flex flex-col gap-1">
              <label htmlFor="roteiro-motivo-voltar" className="text-sm font-semibold">Motivo da alteração</label>
              <Textarea id="roteiro-motivo-voltar" value={motivoVoltar} maxLength={500} rows={3} onChange={(evento) => setMotivoVoltar(evento.target.value)} />
              <p className="text-xs text-muted-foreground">Mínimo de {MOTIVO_MINIMO} caracteres.</p>
            </div>
            {erroVoltar ? <p role="alert" className="text-sm text-destructive">{erroVoltar}</p> : null}
          </FormDialogBody>
          <FormDialogFooter>
            <Button type="button" variant="outline" disabled={voltando} onClick={() => setVoltarAberto(false)}>Cancelar</Button>
            <Button type="button" disabled={voltando || motivoVoltar.trim().length < MOTIVO_MINIMO} onClick={() => void confirmarVoltar()}>{voltando ? 'Salvando...' : ROTULO_VOLTAR_AO_MODELO}</Button>
          </FormDialogFooter>
        </FormDialogContent>
      </FormDialog>

      <FormDialog open={salvarAberto} onOpenChange={(aberto) => { if (!salvando) setSalvarAberto(aberto); }}>
        <FormDialogContent>
          <FormDialogHeader>
            <FormDialogTitle>Salvar nova versão do roteiro</FormDialogTitle>
            <FormDialogDescription>Diga por que o roteiro mudou. O motivo fica no histórico com o seu nome.</FormDialogDescription>
          </FormDialogHeader>
          <FormDialogBody>
            <div className="flex flex-col gap-1">
              <label htmlFor="roteiro-motivo" className="text-sm font-semibold">Motivo da alteração</label>
              <Textarea id="roteiro-motivo" value={motivo} maxLength={500} rows={3} onChange={(evento) => setMotivo(evento.target.value)} />
              <p className="text-xs text-muted-foreground">Mínimo de {MOTIVO_MINIMO} caracteres.</p>
            </div>
            {erroSalvar ? <p role="alert" className="text-sm text-destructive">{erroSalvar}</p> : null}
          </FormDialogBody>
          <FormDialogFooter>
            <Button type="button" variant="outline" disabled={salvando} onClick={() => setSalvarAberto(false)}>Voltar</Button>
            <Button type="button" disabled={salvando || !motivoValido} onClick={() => void confirmarSalvar()}>{salvando ? 'Salvando...' : 'Salvar versão'}</Button>
          </FormDialogFooter>
        </FormDialogContent>
      </FormDialog>
    </div>
  );
}
